import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { translate, pluralize, detectLanguage } from '../i18n';

interface I18nContextValue {
  lang: string;
  setLang: (lang: string) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  n: (baseKey: string, count: number) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<string>(() => {
    try {
      return detectLanguage();
    } catch {
      return 'en';
    }
  });

  const setLang = useCallback((code: string) => {
    localStorage.setItem('lang', code);
    setLangState(code);
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => translate(lang, key, vars),
    [lang],
  );

  const n = useCallback(
    (baseKey: string, count: number) => pluralize(lang, baseKey, count),
    [lang],
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, t, n }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside I18nProvider');
  return ctx;
}
